import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Vessel {
  id: number;
  name: string;
  model?: string;
  type?: string;
}

const RoutineCheckList: React.FC = () => {
  const navigate = useNavigate();
  const [lang, setLang] = useState<'el' | 'en'>(
    (sessionStorage.getItem('yacht_lang') as 'el' | 'en') || 'el'
  );
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    sessionStorage.setItem('yacht_lang', lang);
  }, [lang]);

  useEffect(() => {
    const fetchVessels = async () => {
      try {
        const response = await fetch('/api/vessels.php');
        if (response.ok) {
          const data = await response.json();
          setVessels(data.vessels || data || []);
        }
      } catch (error) {
        console.error('Error loading vessels:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchVessels();
  }, []);

  const t = (el: string, en: string) => (lang === 'el' ? el : en);

  const filtered = vessels.filter((v) => {
    const q = search.toLowerCase();
    return (
      (v.name || '').toLowerCase().includes(q) ||
      (v.model || '').toLowerCase().includes(q) ||
      (v.type || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-[#f3f4f6]">
      {/* Header */}
      <div className="bg-[#1e40af] p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 hover:bg-[#1e3a8a] rounded-lg transition-colors"
            >
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-bold text-white">
                🔧 {t('ΕΛΕΓΧΟΣ ΡΟΥΤΙΝΑΣ', 'ROUTINE CHECK')}
              </h1>
              <p className="text-[#dbeafe] text-sm">
                {t('Επιλέξτε σκάφος', 'Select vessel')}
              </p>
            </div>
          </div>
          <button
            onClick={() => setLang(lang === 'el' ? 'en' : 'el')}
            className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-sm text-white transition-colors"
          >
            {lang === 'el' ? 'EN' : 'EL'}
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="p-4 bg-white border-b border-[#d1d5db] shadow-sm">
        <input
          type="text"
          placeholder={t('Αναζήτηση σκάφους...', 'Search vessel...')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full px-4 py-2 bg-white border border-[#d1d5db] rounded-lg text-[#374151] text-sm focus:ring-2 focus:ring-[#1e40af] focus:border-[#1e40af] focus:outline-none"
        />
      </div>

      {/* Table */}
      <div className="p-4">
        {loading ? (
          <p className="text-center text-gray-500 py-8">
            {t('Φόρτωση...', 'Loading...')}
          </p>
        ) : filtered.length === 0 ? (
          <p className="text-center text-gray-500 py-8">
            {t('Δεν βρέθηκαν σκάφη', 'No vessels found')}
          </p>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#f3f4f6] border-b border-[#d1d5db]">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#374151] uppercase">
                      {t('Σκάφος', 'Vessel')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#374151] uppercase">
                      {t('Μοντέλο', 'Model')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#374151] uppercase">
                      {t('Τύπος', 'Type')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-[#374151] uppercase">
                      {t('Ενέργειες', 'Actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((v) => (
                    <tr
                      key={v.id}
                      className="border-b border-[#e5e7eb] hover:bg-[#f9fafb] transition-colors"
                    >
                      <td className="px-4 py-3 text-sm font-medium text-[#374151]">
                        🛥️ {v.name}
                      </td>
                      <td className="px-4 py-3 text-sm text-[#6b7280]">
                        {v.model || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-[#6b7280]">
                        {v.type || '-'}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          onClick={() => navigate(`/routine-check/new/${v.id}`)}
                          className="px-3 py-1 bg-[#1e40af] hover:bg-[#1e3a8a] text-white text-sm rounded-lg transition-colors mr-2"
                        >
                          {t('Νέος Έλεγχος', 'New Check')}
                        </button>
                        <button
                          onClick={() => navigate(`/routine-check/history/${v.id}`)}
                          className="px-3 py-1 bg-[#6b7280] hover:bg-[#4b5563] text-white text-sm rounded-lg transition-colors"
                        >
                          {t('Ιστορικό', 'History')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoutineCheckList;
